import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import type { NumberFormatValues, SourceInfo } from "react-number-format";

import { Input } from "../Input/Input";
import type { InputProps } from "../Input/Input";
import { ClearStake } from "../Icon/ClearStake";

import { getCurrencySymbol } from "./CurrencyInput.utils";

import * as styles from "./CurrencyInput.module.scss";

export type CurrencyInputProps = {
  currency: Intl.NumberFormatOptions["currency"];
  value: number | undefined;
  onChange: (value: number) => void;
  canClear?: boolean;
  renderClearIcon?: (iconProps: {
    onClick: () => void;
    onMouseDown: (event: React.MouseEvent) => void;
  }) => React.ReactNode;
  onClear?: () => void;
  /** highest value that can be entered (default 9,999,999.99) */
  allowUpTo?: number;
} & Omit<InputProps, "value" | "onChange" | "defaultValue" | "type">;

const CurrencyInput = ({
  value: externalValue,
  currency,
  onChange,
  renderClearIcon,
  canClear = true,
  onClear,
  onBlur,
  allowUpTo = 9_999_999.99,
  ...props
}: CurrencyInputProps) => {
  const prefix = getCurrencySymbol(currency ?? "");

  // an empty floatValue means the box is empty or half-typed, so let it through
  const isAllowed = ({ floatValue }: NumberFormatValues) =>
    floatValue === undefined || floatValue <= allowUpTo;

  // keep the display as a string so half-typed values like "0", "0." and "0.5"
  // hang around. As a plain number they'd all snap back to 0.
  const [displayValue, setDisplayValue] = useState(
    externalValue ? externalValue.toFixed(2) : "",
  );

  // pick up outside changes (quick-stake buttons, resets) but leave whatever
  // the user is typing alone. If we're already on the same number, do nothing.
  // Watching externalValue only is deliberate — displayValue is what we compare
  // against, not something we want to react to.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const current = displayValue === "" ? undefined : Number(displayValue);
    if (externalValue === current) return;
    setDisplayValue(externalValue ? externalValue.toFixed(2) : "");
  }, [externalValue]);

  const handleValueChange = (
    values: NumberFormatValues,
    { source }: SourceInfo,
  ) => {
    // only care about real typing, not the value prop syncing back in
    if (source !== "event") return;
    // turn a leading "." into "0." so ".5" comes out as 0.5
    const next = values.value.startsWith(".")
      ? `0${values.value}`
      : values.value;
    setDisplayValue(next);
    onChange(values.floatValue ?? 0);
  };

  // on blur, round out to 2dp (".5" -> "0.50") so the trailing zero sticks
  // when they come back. Empty or 0 just drops back to the placeholder.
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const num = displayValue === "" ? 0 : Number(displayValue);
    setDisplayValue(num ? num.toFixed(2) : "");
    onBlur?.(event);
  };

  const clearProps = {
    onClick: () => {
      onClear?.();
      onChange(0);
    },
    onMouseDown: (event: React.MouseEvent) => event.preventDefault(),
  };

  const shouldHideClear =
    (!props.errorMessage && displayValue === "") || props.disabled;

  const icon =
    canClear && !shouldHideClear ? (
      renderClearIcon ? (
        renderClearIcon(clearProps)
      ) : (
        <ClearStake className={styles.clear} {...clearProps} />
      )
    ) : null;

  return (
    <NumericFormat
      // run it through our Input atom so we keep the label/icon/error styling
      customInput={Input}
      // controlled by the string so typing "0.50" works; empty shows placeholder
      value={displayValue}
      valueIsNumericString
      onValueChange={handleValueChange}
      onBlur={handleBlur}
      isAllowed={isAllowed}
      thousandSeparator
      decimalScale={2}
      allowNegative={false}
      prefix={prefix}
      placeholder={`${prefix}0`}
      inputMode="decimal"
      icon={icon}
      {...props}
    />
  );
};

export { CurrencyInput };
